import { createBrowserRouter } from 'react-router';
import { Protected } from './components/Protected';
import { RouteError } from './components/RouteError';
import { LoginPage } from './pages/auth/LoginPage';
import { VerifyCodePage } from './pages/auth/VerifyCodePage';
import { SetupTwoFactorPage } from './pages/auth/SetupTwoFactorPage';
import { ChangePasswordPage } from './pages/auth/ChangePasswordPage';
import { DashboardPage } from './pages/DashboardPage';
import { BranchesPage } from './pages/BranchesPage';
import { UsersPage } from './pages/UsersPage';
import { AlertsPage } from './pages/AlertsPage';
import { AccountPage } from './pages/AccountPage';
import { PlanPage } from './pages/PlanPage';
import { NotFoundPage } from './pages/NotFoundPage';
import { Gate } from './components/Gate';
import { PriceListsPage } from './pages/wholesale/PriceListsPage';
import { CommissionsPage } from './pages/wholesale/CommissionsPage';
import { DriverPage } from './pages/wholesale/DriverPage';
import { RoutesPage } from './pages/wholesale/RoutesPage';
import { WholesaleCustomersPage } from './pages/wholesale/WholesaleCustomersPage';
import { WholesaleMoneyPage } from './pages/wholesale/WholesaleMoneyPage';
import { WholesaleOrdersPage } from './pages/wholesale/WholesaleOrdersPage';
import { WholesaleSettingsPage } from './pages/wholesale/WholesaleSettingsPage';
import { WholesaleStatsPage } from './pages/wholesale/WholesaleStatsPage';
import { ProductsPage } from './pages/products/ProductsPage';
import { PricesPage } from './pages/PricesPage';
import { SuppliersPage } from './pages/SuppliersPage';
import { PurchasesPage } from './pages/purchases/PurchasesPage';
import { NewPurchasePage } from './pages/purchases/NewPurchasePage';
import { PurchaseDetailPage } from './pages/purchases/PurchaseDetailPage';
import { StockPage } from './pages/StockPage';
import { TransfersPage } from './pages/TransfersPage';
import { PosPage } from './pages/pos/PosPage';
import { CashPage } from './pages/CashPage';
import { SalesPage } from './pages/SalesPage';
import { PosSettingsPage } from './pages/settings/PosSettingsPage';
import { FranchisesPage } from './pages/franchise/FranchisesPage';
import { MyFranchisePage } from './pages/franchise/MyFranchisePage';
import { CustomersPage } from './pages/customers/CustomersPage';
import { CustomerPage } from './pages/customers/CustomerPage';
import { WastePage } from './pages/production/WastePage';
import { ProductionPage } from './pages/production/ProductionPage';
import { CuttingPage } from './pages/cutting/CuttingPage';
import { WorkersPage } from './pages/cutting/WorkersPage';
import { ControlPage } from './pages/control/ControlPage';
import { StatsPage } from './pages/StatsPage';
import { ForgotPasswordPage, ResetPasswordPage } from './pages/auth/ResetPasswordPages';
import { PayablesPage } from './pages/payables/PayablesPage';
import { SupplierAccountPage } from './pages/payables/SupplierAccountPage';
import { EmployeesPage } from './pages/employees/EmployeesPage';
import { EmployeePage } from './pages/employees/EmployeePage';
import { AttendancePage } from './pages/employees/AttendancePage';
import { ChatPage } from './pages/orders-chat/ChatPage';
import { OrdersPage } from './pages/orders-chat/OrdersPage';
import { OrdersSettingsPage } from './pages/orders-chat/OrdersSettingsPage';
import { BifiStatsPage } from './pages/orders-chat/BifiStatsPage';
import { ConnectWhatsAppPage } from './pages/orders-chat/ConnectWhatsAppPage';

export const router = createBrowserRouter([
  {
    errorElement: <RouteError />,
    children: [
      { path: '/ingresar', element: <LoginPage /> },
      { path: '/ingresar/codigo', element: <VerifyCodePage /> },
      { path: '/activar-2fa', element: <SetupTwoFactorPage /> },
      { path: '/cambiar-clave', element: <ChangePasswordPage /> },
      { path: '/recuperar', element: <ForgotPasswordPage /> },
      { path: '/recuperar/nueva', element: <ResetPasswordPage /> },
      {
        element: <Protected />,
        children: [
          { index: true, element: <DashboardPage /> },
          { path: 'alertas', element: <AlertsPage /> },
          {
            path: 'chat',
            element: (
              <Gate any={['orders.manage']}>
                <ChatPage />
              </Gate>
            ),
          },
          {
            path: 'pedidos',
            element: (
              <Gate any={['orders.manage']}>
                <OrdersPage />
              </Gate>
            ),
          },
          {
            path: 'conectar-whatsapp',
            element: (
              <Gate any={['settings.manage', 'franchise.accounts']}>
                <ConnectWhatsAppPage />
              </Gate>
            ),
          },
          {
            path: 'pedidos/estadisticas',
            element: (
              <Gate any={['orders.manage', 'settings.manage', 'franchise.accounts']}>
                <BifiStatsPage />
              </Gate>
            ),
          },
          {
            path: 'pedidos/configuracion',
            element: (
              <Gate any={['settings.manage', 'franchise.accounts']}>
                <OrdersSettingsPage />
              </Gate>
            ),
          },
          ...(
            [
              ['mayorista', <WholesaleOrdersPage key="o" />, ['wholesale.manage', 'wholesale.sell']],
              ['mayorista/clientes', <WholesaleCustomersPage key="c" />, ['wholesale.manage', 'wholesale.sell']],
              ['mayorista/listas', <PriceListsPage key="l" />, ['wholesale.manage']],
              ['mayorista/rutas', <RoutesPage key="r" />, ['wholesale.manage', 'wholesale.sell', 'wholesale.deliver']],
              ['reparto', <DriverPage key="d" />, ['wholesale.manage', 'wholesale.sell', 'wholesale.deliver']],
              ['mayorista/cobros', <WholesaleMoneyPage key="m" />, ['wholesale.manage', 'wholesale.sell', 'wholesale.deliver']],
              ['mayorista/comisiones', <CommissionsPage key="k" />, ['wholesale.manage', 'wholesale.sell', 'wholesale.deliver']],
              ['mayorista/estadisticas', <WholesaleStatsPage key="s" />, ['wholesale.manage']],
              ['mayorista/configuracion', <WholesaleSettingsPage key="g" />, ['wholesale.manage']],
            ] as const
          ).map(([path, page, caps]) => ({ path, element: <Gate any={[...caps]}>{page}</Gate> })),
          { path: 'productos', element: <ProductsPage /> },
          { path: 'precios', element: <PricesPage /> },
          {
            path: 'proveedores',
            element: (
              <Gate any={['suppliers.manage', 'purchases.manage']}>
                <SuppliersPage />
              </Gate>
            ),
          },
          {
            path: 'proveedores/pagos',
            element: (
              <Gate any={['suppliers.manage']}>
                <PayablesPage />
              </Gate>
            ),
          },
          {
            path: 'proveedores/:id',
            element: (
              <Gate any={['suppliers.manage']}>
                <SupplierAccountPage />
              </Gate>
            ),
          },
          {
            path: 'empleados',
            element: (
              <Gate any={['employees.manage', 'employees.advances']}>
                <EmployeesPage />
              </Gate>
            ),
          },
          {
            path: 'empleados/:id',
            element: (
              <Gate any={['employees.manage', 'employees.advances']}>
                <EmployeePage />
              </Gate>
            ),
          },
          {
            path: 'asistencia',
            element: (
              <Gate any={['employees.manage', 'employees.advances']}>
                <AttendancePage />
              </Gate>
            ),
          },
          {
            path: 'compras',
            element: (
              <Gate any={['purchases.manage']}>
                <PurchasesPage />
              </Gate>
            ),
          },
          {
            path: 'compras/nueva',
            element: (
              <Gate any={['purchases.manage']}>
                <NewPurchasePage />
              </Gate>
            ),
          },
          {
            path: 'compras/:id',
            element: (
              <Gate any={['purchases.manage']}>
                <PurchaseDetailPage />
              </Gate>
            ),
          },
          {
            path: 'stock',
            element: (
              <Gate any={['purchases.manage', 'stock.waste']}>
                <StockPage />
              </Gate>
            ),
          },
          {
            path: 'transferencias',
            element: (
              <Gate any={['transfers.manage']}>
                <TransfersPage />
              </Gate>
            ),
          },
          {
            path: 'venta',
            element: (
              <Gate any={['pos.sell']}>
                <PosPage />
              </Gate>
            ),
          },
          {
            path: 'caja',
            element: (
              <Gate any={['pos.sell', 'cash.own']}>
                <CashPage />
              </Gate>
            ),
          },
          {
            path: 'ventas',
            element: (
              <Gate any={['pos.sell', 'sales.void', 'dashboard.view']}>
                <SalesPage />
              </Gate>
            ),
          },
          {
            path: 'mermas',
            element: (
              <Gate any={['stock.waste']}>
                <WastePage />
              </Gate>
            ),
          },
          {
            path: 'estadisticas',
            element: (
              <Gate any={['stats.global']}>
                <StatsPage />
              </Gate>
            ),
          },
          {
            path: 'control',
            element: (
              <Gate any={['purchases.manage', 'audit.view']}>
                <ControlPage />
              </Gate>
            ),
          },
          {
            path: 'desposte',
            element: (
              <Gate any={['cutting.manage']}>
                <CuttingPage />
              </Gate>
            ),
          },
          {
            path: 'despostero',
            element: (
              <Gate any={['cutting.manage', 'suppliers.manage']}>
                <WorkersPage />
              </Gate>
            ),
          },
          {
            path: 'produccion',
            element: (
              <Gate any={['stock.waste']}>
                <ProductionPage />
              </Gate>
            ),
          },
          {
            path: 'clientes',
            element: (
              <Gate any={['pos.sell', 'customers.manage']}>
                <CustomersPage />
              </Gate>
            ),
          },
          {
            path: 'clientes/:id',
            element: (
              <Gate any={['pos.sell', 'customers.manage']}>
                <CustomerPage />
              </Gate>
            ),
          },
          {
            path: 'configuracion',
            element: (
              <Gate any={['settings.manage', 'franchise.accounts']}>
                <PosSettingsPage />
              </Gate>
            ),
          },
          {
            path: 'franquicias',
            element: (
              <Gate any={['branches.manage']}>
                <FranchisesPage />
              </Gate>
            ),
          },
          { path: 'mi-franquicia', element: <MyFranchisePage /> },
          { path: 'sucursales', element: <BranchesPage /> },
          { path: 'usuarios', element: <UsersPage /> },
          { path: 'cuenta', element: <AccountPage /> },
          { path: 'plan', element: <PlanPage /> },
          { path: '*', element: <NotFoundPage /> },
        ],
      },
    ],
  },
]);
